import { useCallback, useEffect, useRef, useState } from 'react';
import portalClient from './portalClient';
import PortalLayout from './PortalLayout';
import { Button, Card, Skeleton, useToast } from '../design-system';
import './Portal.css';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Files shared BOTH ways: what the client sent, and what the firm published
// back. The firm's internal working papers are never in this list — the server
// only returns documents explicitly flagged shared.
function PortalDocuments() {
  const toast = useToast();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState('all'); // all | mine | firm
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await portalClient.get('/api/portal/documents');
      setDocs(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not load your documents');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.danger('That file is larger than the 25MB limit');
      return;
    }
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      // No explicit Content-Type — axios sets it with the multipart boundary.
      await portalClient.post('/api/portal/documents', body, { timeout: 120000 });
      toast.success('Uploaded');
      await load();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const open = async (doc) => {
    try {
      const res = await portalClient.get(`/api/portal/documents/${doc.id}/url`);
      if (res.data?.url) window.open(res.data.url, '_blank', 'noreferrer');
    } catch {
      toast.danger('Could not open that file');
    }
  };

  const visible = docs.filter((d) => {
    if (tab === 'mine') return d.fromClient;
    if (tab === 'firm') return !d.fromClient;
    return true;
  });

  return (
    <PortalLayout
      title="Documents"
      subtitle="Files you've sent, and files your accountant has shared with you."
    >
      <div className="portal-toolbar">
        <div className="portal-tabs">
          {[
            { key: 'all', label: `All (${docs.length})` },
            { key: 'mine', label: `Sent by you (${docs.filter((d) => d.fromClient).length})` },
            { key: 'firm', label: `From your accountant (${docs.filter((d) => !d.fromClient).length})` },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              className={tab === t.key ? 'is-active' : ''}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <input
          ref={fileRef}
          type="file"
          className="portal-file-input"
          onChange={(e) => upload(e.target.files?.[0])}
          disabled={uploading}
        />
        <Button variant="primary" onClick={() => fileRef.current?.click()} loading={uploading}>
          Upload a document
        </Button>
      </div>

      {loading ? (
        <Card><Skeleton.Text lines={4} /></Card>
      ) : visible.length === 0 ? (
        <Card padding="lg">
          <div className="portal-empty">
            <div className="portal-empty-icon">📄</div>
            <p className="portal-empty-title">No documents yet</p>
            <p className="portal-muted">Anything you upload here goes straight to your accountant.</p>
          </div>
        </Card>
      ) : (
        <Card padding="md">
          <ul className="portal-file-list">
            {visible.map((d) => (
              <li key={d.id}>
                <button type="button" className="portal-file-row" onClick={() => open(d)}>
                  <span className="portal-file-name">📄 {d.title}</span>
                  <span className="portal-muted">
                    {d.fromClient ? 'Sent by you' : 'From your accountant'}
                    {d.sizeBytes ? ` · ${fmtSize(d.sizeBytes)}` : ''}
                    {` · ${fmtDate(d.uploadedAt)}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </PortalLayout>
  );
}

export default PortalDocuments;
